// Routeهای اپ کارکنان (IA §2.3). Routeهای ماژول از منو ساخته می‌شوند و RequireAccess همان Permission/Feature منو را دارد.
import {
  AccessProvider,
  ForbiddenState,
  PublicApiProvider,
  RequireAccess,
  RequireAuthenticated,
  RequireTenantSession,
  ScreenLock,
  TenantSelectPage,
  TenantSessionProvider,
} from '@phoenix/auth';
import type { ReactNode } from 'react';
import { Navigate, Outlet, useParams, type RouteObject } from 'react-router';
import { StaffLayout } from './layout/StaffLayout';
import { leafItems, SUBSCRIPTION_PATH } from './navigation';
import { BulkPricePage } from './pages/catalog/BulkPricePage';
import { DiscountsPage } from './pages/catalog/DiscountsPage';
import { PackagesPage } from './pages/catalog/PackagesPage';
import { PricesPage } from './pages/catalog/PricesPage';
import { ServiceDetailPage } from './pages/catalog/ServiceDetailPage';
import { ServicesPage } from './pages/catalog/ServicesPage';
import { SimulatorPage } from './pages/catalog/SimulatorPage';
import { TariffBookPage } from './pages/catalog/TariffBookPage';
import { ContractsPage } from './pages/catalog/ContractsPage';
import { HomePage } from './pages/HomePage';
import { AccessPoliciesPage } from './pages/iam/AccessPoliciesPage';
import { DeviceAccountsPage } from './pages/iam/DeviceAccountsPage';
import { AccountPage } from './pages/iam/AccountPage';
import { BreakGlassReviewPage } from './pages/iam/BreakGlassReviewPage';
import { PatientAccessReportPage } from './pages/iam/PatientAccessReportPage';
import { ApprovalsPage } from './pages/iam/ApprovalsPage';
import { InvitationPage } from './pages/iam/public/InvitationPage';
import { RecoverPage } from './pages/iam/public/RecoverPage';
import { DeviceModePage } from './pages/documents/DeviceModePage';
import { RolesPage } from './pages/iam/RolesPage';
import { SecurityEventsPage } from './pages/iam/SecurityEventsPage';
import { StaffDetailPage } from './pages/iam/StaffDetailPage';
import { STAFF_PATH, StaffListPage } from './pages/iam/StaffListPage';
import { DepartmentsPage } from './pages/organization/DepartmentsPage';
import { DeviceDetailPage } from './pages/organization/DeviceDetailPage';
import { EquipmentDashboardPage } from './pages/organization/EquipmentDashboardPage';
import { DevicesPage } from './pages/organization/DevicesPage';
import { DeviceTypesPage } from './pages/organization/DeviceTypesPage';
import { ResourceBlocksPage } from './pages/organization/ResourceBlocksPage';
import { ResourceStatusPage } from './pages/organization/ResourceStatusPage';
import { WorkOrdersPage } from './pages/organization/WorkOrdersPage';
import { ResourceTagsPage } from './pages/organization/ResourceTagsPage';
import { RoomsPage } from './pages/organization/RoomsPage';
import { DuplicatesPage } from './pages/patients/DuplicatesPage';
import { DataExportPublicPage } from './pages/patients/DataExportPublicPage';
import { DataRequestsPage } from './pages/patients/DataRequestsPage';
import { IncompletePatientsPage } from './pages/patients/IncompletePatientsPage';
import { MergeWizardPage } from './pages/patients/MergeWizardPage';
import { PatientProfilePage } from './pages/patients/PatientProfilePage';
import { PatientActionsProvider } from './pages/patients/PatientQuickActions';
import { PatientSettingsPage } from './pages/patients/PatientSettingsPage';
import { PatientsPage } from './pages/patients/PatientsPage';
import { AllSettingsPage } from './pages/configuration/AllSettingsPage';
import { CustomFieldsPage } from './pages/configuration/CustomFieldsPage';
import { ModulesPage } from './pages/configuration/ModulesPage';
import { ParametersAndRulesPage } from './pages/configuration/ParametersAndRulesPage';
import { SpecialtyPacksPage } from './pages/configuration/SpecialtyPacksPage';
import { BranchesPage } from './pages/settings/BranchesPage';
import { CenterAndBrandingPage } from './pages/settings/BrandingSection';
import { ChecklistPage } from './pages/settings/ChecklistPage';
import { HolidayManagementPage } from './pages/settings/HolidayManagementPage';
import { GeneralAndCalendarPage } from './pages/settings/HolidaysSection';
import { PublicProfilePage } from './pages/settings/PublicProfilePage';
import { SubscriptionPage } from './pages/settings/SubscriptionPage';
import { TerminationPage } from './pages/settings/TerminationPage';
import { SignupPage } from './pages/settings/public/SignupPage';
import {
  ExportDownloadPage,
  OwnershipAcceptPage,
  SubscriptionPaymentReturnPage,
} from './pages/settings/public/SubscriptionPublicPages';
import { InstallationPage } from './pages/settings/InstallationPage';
import { SupportAccessPage } from './pages/settings/SupportAccessPage';
import { AppointmentDetailPage } from './pages/scheduling/AppointmentDetailPage';
import { BulkClosuresPage } from './pages/scheduling/BulkClosuresPage';
import { CalendarPage } from './pages/scheduling/CalendarPage';
import { DaySheetPage } from './pages/scheduling/DaySheetPage';
import { SchedulingRealtimeProvider, type SchedulingRealtimeFactory } from './pages/scheduling/realtime';
import { SchedulingActionsProvider } from './pages/scheduling/SchedulingActions';
import { SchedulesSettingsPage } from './pages/scheduling/SchedulesSettingsPage';
import { AppointmentReportPage } from './pages/scheduling/AppointmentReportPage';
import { BookingPoliciesPage } from './pages/scheduling/BookingPoliciesPage';
import { MySchedulePage } from './pages/scheduling/MySchedulePage';
import { RescheduleQueuePage } from './pages/scheduling/RescheduleQueuePage';
import { WaitlistPage } from './pages/scheduling/WaitlistPage';
import { OnlineRequestsPage } from './pages/scheduling/OnlineRequestsPage';
import { AdmissionRulesPage } from './pages/scheduling/AdmissionRulesPage';
import { BookingChannelsPage } from './pages/scheduling/BookingChannelsPage';
import { ChannelSettingsPage } from './pages/channels/ChannelSettingsPage';
import { MyProviderProfilePage } from './pages/channels/MyProviderProfilePage';
import { ProviderPresencePage } from './pages/channels/ProviderPresencePage';
import { ReviewsModerationPage } from './pages/channels/ReviewsModerationPage';
import { CallsPage } from './pages/channels/CallsPage';
import { ChannelReportPage } from './pages/channels/ChannelReportPage';
import { TicketDetailPage } from './pages/channels/TicketDetailPage';
import { TicketSettingsPage } from './pages/channels/TicketSettingsPage';
import { TicketsPage } from './pages/channels/TicketsPage';
import { GroupSessionsPage } from './pages/scheduling/GroupSessionsPage';
import { ONBOARDING_PERMISSION, SETUP_PATH } from './pages/setup/common';
import { SetupWizardPage } from './pages/setup/SetupWizardPage';
import {
  ImportRealtimeContext,
  signalRImportFactory,
  type ImportRealtimeFactory,
} from './pages/settings/import/common';
import { ImportPage } from './pages/settings/import/ImportPage';
import { DeadLettersPage } from './pages/integrations/DeadLettersPage';
import { IntegrationHealthPage } from './pages/integrations/IntegrationHealthPage';
import { IntegrationsPage } from './pages/integrations/IntegrationsPage';
import { BranchInsurerSitesPage } from './pages/integrations/BranchInsurerSitesPage';
import { CredentialRenewalPage } from './pages/integrations/CredentialRenewalPage';
import { EntityCallLogPage } from './pages/integrations/EntityCallLogPage';
import { InsurerCredentialsPage } from './pages/integrations/InsurerCredentialsPage';
import { LocalAgentsPage } from './pages/integrations/LocalAgentsPage';
import { BulkNoticesPage } from './pages/messaging/BulkNoticesPage';
import { ChannelsPage } from './pages/messaging/ChannelsPage';
import { InboundPage } from './pages/messaging/InboundPage';
import { MessageLogPage } from './pages/messaging/MessageLogPage';
import {
  NotificationRealtimeProvider,
  signalRNotificationFactory,
  type NotificationRealtimeFactory,
} from './pages/messaging/NotificationCenter';
import { RulesPage } from './pages/messaging/RulesPage';
import { TemplatesPage } from './pages/messaging/TemplatesPage';
import { ComingSoonPage, NotFoundPage, RouteErrorPage } from './pages/SystemPages';
import { ConsentTemplatesPage } from './pages/documents/ConsentTemplatesPage';
import { DocumentSettingsPage } from './pages/documents/DocumentSettingsPage';
import { FormSubmissionPage } from './pages/documents/FormSubmissionPage';
import { FormTemplatesPage } from './pages/documents/FormTemplatesPage';
import { FormStudioPage, PublicStudioFormPage } from './pages/documents/FormStudioPage';
import { PrintTemplatesPage } from './pages/documents/PrintTemplatesPage';
import { RetentionPage } from './pages/documents/RetentionPage';
import {
  PublicFormPage,
  PublicSharePage,
  PublicSignaturePage,
} from './pages/documents/public/DocumentPublicPages';
import { TabletModePage } from './pages/documents/TabletModePage';
import { BanksPage } from './pages/accounting/BanksPage';
import { ChequesPage as AccChequesPage } from './pages/accounting/ChequesPage';
import { ExpensesPage } from './pages/accounting/ExpensesPage';
import { ChartPage } from './pages/accounting/ChartPage';
import { DailyControlPage } from './pages/accounting/DailyControlPage';
import { ExportsPage } from './pages/accounting/ExportsPage';
import { FiscalPage } from './pages/accounting/FiscalPage';
import { JournalDetailPage, JournalsPage } from './pages/accounting/JournalsPage';
import { MappingsPage } from './pages/accounting/MappingsPage';
import { PostingErrorsPage } from './pages/accounting/PostingErrorsPage';
import { ReportsPage } from './pages/accounting/ReportsPage';
import { JournalTemplatesPage } from './pages/accounting/JournalTemplatesPage';
import { LedgerReportsPage } from './pages/accounting/LedgerReportsPage';
import { ManualJournalsPage } from './pages/accounting/ManualJournalsPage';
import { BankReconciliationPage } from './pages/accounting/BankReconciliationPage';
import { TelehealthSettingsPage } from './pages/telehealth/TelehealthSettingsPage';
import { WaitingRoomPage } from './pages/telehealth/WaitingRoomPage';
import { BillingSettingsPage } from './pages/billing/BillingSettingsPage';
import { CashierPage } from './pages/billing/CashierPage';
import { CashReportPage } from './pages/billing/CashReportPage';
import { CashSessionPage } from './pages/billing/CashSessionPage';
import { DepositsPage } from './pages/billing/DepositsPage';
import { PatientFinancePage } from './pages/billing/PatientFinancePage';
import { PublicPayPage } from './pages/billing/PublicPayPage';
import { RefundsPage } from './pages/billing/RefundsPage';
import { OfflineDeskPage } from './pages/billing/OfflineDeskPage';
import { SyncConflictsPage } from './pages/billing/SyncConflictsPage';
import { InvoicePage, InvoicesPage } from './pages/billing/InvoicesPage';
import { VisitPage } from './pages/billing/VisitPage';
import { DeferredRevenuePage } from './pages/billing/DeferredRevenuePage';
import { PackageSalesPage } from './pages/billing/PackagesPage';
import { QuoteDetailPage, QuoteEditorPage, QuotesPage } from './pages/billing/QuotesPage';
import { VisitByAppointmentPage, VisitsPage } from './pages/billing/VisitsPage';
import { ChequesPage } from './pages/billing/ChequesPage';
import { InstallmentsPage } from './pages/billing/InstallmentsPage';
import { ProductSalePage } from './pages/billing/ProductSalePage';
import { EInvoiceQueuePage } from './pages/billing/EInvoiceQueuePage';
import { OrganizationAccountsPage } from './pages/billing/OrganizationAccountsPage';
import { ReceivablesReportPage } from './pages/billing/ReceivablesReportPage';
import { ReconciliationPage } from './pages/billing/ReconciliationPage';
import { ClinicalSettingsPage } from './pages/clinical/ClinicalSettingsPage';
import { EncounterPage } from './pages/clinical/EncounterPage';
import { PendingTasksPage } from './pages/clinical/PendingTasksPage';
import { ClinicalRemindersPage } from './pages/clinical/ClinicalRemindersPage';
import { AdverseEventsReportPage } from './pages/clinical/AdverseEvents';
import { TreatmentPlanPage } from './pages/clinical/TreatmentPlans';
import { WorkbenchPage } from './pages/clinical/WorkbenchPage';
import { SharedFieldsPage } from './pages/documents/SharedFieldsPage';
import { MobileUploadPage } from './pages/documents/MobileUploadPage';
import { CalculatorLibraryPage } from './pages/configuration/CalculatorLibraryPage';
import { MedSettingsPage } from './pages/medorders/MedSettingsPage';
import { ImagingViewerPage } from './pages/medorders/ImagingViewerPage';
import { NationalQueuePage } from './pages/medorders/NationalQueuePage';
import { RxMonitorPage } from './pages/medorders/RxMonitorPage';
import { NursingCartablePage } from './pages/medorders/NursingCartablePage';
import { RxOnlyVisitPage } from './pages/medorders/RxOnlyVisitPage';
import { ApprovalInboxPage, MyApprovalsPage } from './pages/medorders/ApprovalPages';
import { NationalReferralsPage } from './pages/medorders/NationalReferralsPage';
import { InsurerAdmissionOrdersPage } from './pages/medorders/InsurerReferralPanels';
import { ReferralCapacityPage } from './pages/scheduling/ReferralCapacityPage';
import { NationalReferralConnectionPage } from './pages/integrations/NationalReferralConnectionPage';
import { OrdersResultsPage } from './pages/medorders/OrdersResultsPage';
import { PublicVerifyPage } from './pages/medorders/public/VerifyPage';
import { PublicCertificateVerifyPage } from './pages/documents/public/CertificateVerifyPage';
import { ReferralsPage } from './pages/medorders/ReferralsPage';
import { UnmatchedResultsPage } from './pages/medorders/UnmatchedResultsPage';
import { ItemDetailPage } from './pages/inventory/ItemDetailPage';
import { ItemsPage } from './pages/inventory/ItemsPage';
import { SuppliersPage } from './pages/inventory/SuppliersPage';
import { AlertsRecallsPage } from './pages/inventory/AlertsRecallsPage';
import { InventoryReportsPage } from './pages/inventory/InventoryReportsPage';
import { InventorySettingsPage } from './pages/inventory/InventorySettingsPage';
import { OpenContainersPage } from './pages/inventory/OpenContainersPage';
import { PurchasingPage } from './pages/inventory/PurchasingPage';
import { StockDocumentsPage } from './pages/inventory/StockDocumentsPage';
import { StocktakePage, StocktakesPage } from './pages/inventory/StocktakesPage';
import { WarehousesStockPage } from './pages/inventory/WarehousesStockPage';
import { AuthorizationsPage } from './pages/insurance/AuthorizationsPage';
import { InsurersPage } from './pages/insurance/InsurersPage';
import { PlansPage as InsPlansPage } from './pages/insurance/PlansPage';
import { ClaimsWorkbenchPage } from './pages/insurance/ClaimsWorkbenchPage';
import { DeductionsPage } from './pages/insurance/DeductionsPage';
import { RemittancesPage } from './pages/insurance/RemittancesPage';
import { ClaimReportsPage } from './pages/insurance/ClaimReportsPage';
import { AgreementsPage } from './pages/compensation/AgreementsPage';
import { LedgerPage } from './pages/compensation/LedgerPage';
import { ShareReportPage } from './pages/compensation/ShareReportPage';
import { PayoutsPage } from './pages/compensation/PayoutsPage';
import { ProviderPortalPage } from './pages/compensation/ProviderPortalPage';
import { ReferrersPage } from './pages/compensation/ReferrersPage';
import { SimulatorPage as CmpSimulatorPage } from './pages/compensation/SimulatorPage';
import { StatementsPage } from './pages/compensation/StatementsPage';
import { ConsultationPage } from './pages/engagement/ConsultationPage';
import { LeadDetailPage } from './pages/engagement/LeadDetailPage';
import { LeadsPage } from './pages/engagement/LeadsPage';
import { QueuePage } from './pages/engagement/QueuePage';
import { FollowUpRulesPage } from './pages/engagement/FollowUpRulesPage';
import { SurveysPage } from './pages/engagement/SurveysPage';
import { PublicSurveyPage } from './pages/engagement/public/PublicSurveyPage';

/** صفحات پیاده‌شده به ازای شناسه آیتم منو (بقیه placeholder «در دست ساخت»). */
const PAGES: Readonly<Record<string, ReactNode>> = {
  center: <CenterAndBrandingPage />,
  branches: <BranchesPage />,
  general: <GeneralAndCalendarPage />,
  'holiday-management': <HolidayManagementPage />,
  // AP-PLT-05.
  checklist: <ChecklistPage />,
  'public-profile': <PublicProfilePage />,
  'support-access': <SupportAccessPage />,
  // AP-PLT-08: نصب On-prem (لایسنس و بسته داده مرجع).
  installation: <InstallationPage />,
  subscription: <SubscriptionPage />,
  termination: <TerminationPage />,
  configuration: <AllSettingsPage />,
  modules: <ModulesPage />,
  'custom-fields': <CustomFieldsPage />,
  // AP-CFG-01.
  'specialty-packs': <SpecialtyPacksPage />,
  'parameters-rules': <ParametersAndRulesPage />,
  staff: <StaffListPage />,
  roles: <RolesPage />,
  'access-policies': <AccessPoliciesPage />,
  'device-accounts': <DeviceAccountsPage />,
  audit: <SecurityEventsPage />,
  approvals: <ApprovalsPage />,
  departments: <DepartmentsPage />,
  rooms: <RoomsPage />,
  'resource-tags': <ResourceTagsPage />,
  // AP-ORG-02.
  'device-types': <DeviceTypesPage />,
  devices: <DevicesPage />,
  'resource-blocks': <ResourceBlocksPage />,
  'resource-status': <ResourceStatusPage />,
  'work-orders': <WorkOrdersPage />,
  // AP-ORG-03.
  'equipment-dashboard': <EquipmentDashboardPage />,
  services: <ServicesPage />,
  prices: <PricesPage />,
  'bulk-prices': <BulkPricePage />,
  // AP-CAT-03.
  packages: <PackagesPage />,
  discounts: <DiscountsPage />,
  'price-simulator': <SimulatorPage />,
  // AP-CAT-04.
  'tariff-book': <TariffBookPage />,
  'organization-contracts': <ContractsPage />,
  patients: <PatientsPage />,
  'patient-settings': <PatientSettingsPage />,
  today: <DaySheetPage />,
  calendar: <CalendarPage />,
  'bulk-closures': <BulkClosuresPage />,
  schedules: <SchedulesSettingsPage />,
  // AP-SCH-02.
  'reschedule-queue': <RescheduleQueuePage />,
  waitlist: <WaitlistPage />,
  // AP-CHN-01.
  'online-requests': <OnlineRequestsPage />,
  channels: <ChannelSettingsPage />,
  'provider-presence': <ProviderPresencePage />,
  'my-provider-profile': <MyProviderProfilePage />,
  reviews: <ReviewsModerationPage />,
  // AP-CHN-02.
  tickets: <TicketsPage />,
  calls: <CallsPage />,
  'channel-report': <ChannelReportPage />,
  'ticket-settings': <TicketSettingsPage />,
  // AP-CLN-03.
  'group-sessions': <GroupSessionsPage />,
  'my-schedule': <MySchedulePage />,
  'appointment-report': <AppointmentReportPage />,
  'booking-policies': <BookingPoliciesPage />,
  import: <ImportPage />,
  integrations: <IntegrationsPage />,
  'booking-channels': <BookingChannelsPage />,
  'admission-rules': <AdmissionRulesPage />,
  'integration-health': <IntegrationHealthPage />,
  'dead-letters': <DeadLettersPage />,
  'local-agents': <LocalAgentsPage />,
  // AP-INT-10: اعتبارنامه‌های من نزد بیمه‌گر و اتصال شعبه به مرکز سیام.
  'insurer-credentials': <InsurerCredentialsPage />,
  'branch-insurer-sites': <BranchInsurerSitesPage />,
  'break-glass-reviews': <BreakGlassReviewPage />,
  // AP-MSG-01.
  'messaging-channels': <ChannelsPage />,
  'messaging-templates': <TemplatesPage />,
  'messaging-rules': <RulesPage />,
  'inbound-messages': <InboundPage />,
  'message-log': <MessageLogPage />,
  'bulk-notices': <BulkNoticesPage />,
  // AP-DOC-01.
  'form-templates': <FormTemplatesPage />,
  'form-studio': <FormStudioPage />,
  'consent-templates': <ConsentTemplatesPage />,
  'print-templates': <PrintTemplatesPage />,
  'document-settings': <DocumentSettingsPage />,
  // AP-DOC-03 (SCR-STAFF-607).
  'document-retention': <RetentionPage />,
  // AP-BIL-01.
  visits: <VisitsPage />,
  invoices: <InvoicesPage />,
  'billing-settings': <BillingSettingsPage />,
  // AP-BIL-02.
  cashier: <CashierPage />,
  'cash-session': <CashSessionPage />,
  refunds: <RefundsPage />,
  'offline-desk': <OfflineDeskPage />,
  'sync-conflicts': <SyncConflictsPage />,
  deposits: <DepositsPage />,
  'cash-report': <CashReportPage />,
  // AP-BIL-03.
  'package-sales': <PackageSalesPage />,
  quotes: <QuotesPage />,
  'deferred-revenue': <DeferredRevenuePage />,
  // AP-BIL-04.
  installments: <InstallmentsPage />,
  cheques: <ChequesPage />,
  reconciliation: <ReconciliationPage />,
  'product-sale': <ProductSalePage />,
  receivables: <ReceivablesReportPage />,
  // AP-BIL-05.
  'bil-e-invoices': <EInvoiceQueuePage />,
  'bil-organization-accounts': <OrganizationAccountsPage />,
  // AP-ACC-01.
  // AP-INV-01.
  'inv-items': <ItemsPage />,
  'inv-suppliers': <SuppliersPage />,
  // AP-INV-02.
  'inv-stock': <WarehousesStockPage />,
  'inv-documents': <StockDocumentsPage />,
  'inv-open-containers': <OpenContainersPage />,
  'inv-alerts': <AlertsRecallsPage />,
  'inv-stocktakes': <StocktakesPage />,
  // AP-INV-03.
  'inv-purchasing': <PurchasingPage />,
  'inv-reports': <InventoryReportsPage />,
  'inv-settings': <InventorySettingsPage />,
  // AP-CMP-01.
  'eng-queue': <QueuePage />,
  'eng-leads': <LeadsPage />,
  // AP-ENG-02.
  'eng-followup-rules': <FollowUpRulesPage />,
  'eng-surveys': <SurveysPage />,
  'ins-insurers': <InsurersPage />,
  'ins-plans': <InsPlansPage />,
  'ins-authorizations': <AuthorizationsPage />,
  'ins-claims': <ClaimsWorkbenchPage />,
  'ins-deductions': <DeductionsPage />,
  'ins-remittances': <RemittancesPage />,
  'ins-claim-reports': <ClaimReportsPage />,
  'cmp-agreements': <AgreementsPage />,
  'cmp-ledger': <LedgerPage />,
  'cmp-report': <ShareReportPage />,
  'cmp-simulator': <CmpSimulatorPage />,
  'cmp-referrers': <ReferrersPage />,
  'cmp-statements': <StatementsPage />,
  'cmp-payouts': <PayoutsPage />,
  'cmp-portal': <ProviderPortalPage />,
  'acc-journals': <JournalsPage />,
  'acc-control': <DailyControlPage />,
  'acc-errors': <PostingErrorsPage />,
  'acc-exports': <ExportsPage />,
  'acc-reports': <ReportsPage />,
  'acc-chart': <ChartPage />,
  'acc-mappings': <MappingsPage />,
  'acc-fiscal': <FiscalPage />,
  'acc-banks': <BanksPage />,
  // AP-TEL-01.
  'tel-waiting-room': <WaitingRoomPage />,
  'tel-settings': <TelehealthSettingsPage />,
  // AP-ACC-03.
  'acc-manual-journals': <ManualJournalsPage />,
  'acc-journal-templates': <JournalTemplatesPage />,
  'acc-reconciliation': <BankReconciliationPage />,
  'acc-ledger-reports': <LedgerReportsPage />,
  // AP-ACC-02.
  'acc-expenses': <ExpensesPage />,
  'acc-cheques': <AccChequesPage />,
  // AP-CLN-01.
  desk: <WorkbenchPage />,
  'clinical-pending': <PendingTasksPage />,
  'clinical-reminders': <ClinicalRemindersPage />,
  'shared-fields': <SharedFieldsPage />,
  'calculator-library': <CalculatorLibraryPage />,
  // AP-CLN-02.
  'clinical-adverse': <AdverseEventsReportPage />,
  'clinical-settings': <ClinicalSettingsPage />,
  // AP-MED-01.
  'med-orders': <OrdersResultsPage />,
  'med-referrals': <ReferralsPage />,
  'med-settings': <MedSettingsPage />,
  // AP-MED-02: صف خطای ارسال ملی (SCR-STAFF-701).
  'med-national-queue': <NationalQueuePage />,
  // AP-INT-05: صف نتایج نامعلوم (SCR-STAFF-703).
  'med-inbound-lab': <UnmatchedResultsPage />,
  // AP-MED-03: ارجاع ملی و کدهای رهگیری (SCR-STAFF-707)، دستور بستری تامین (SCR-STAFF-716)، ظرفیت ارجاعی سیب
  // (SCR-STAFF-317، 318) و اتصال ارجاع ملی (SCR-STAFF-1005).
  'med-national-referrals': <NationalReferralsPage />,
  // AP-MED-04: پایش ارسال‌ها (SCR-STAFF-710)، ویزیت فقط نسخه (SCR-STAFF-711)، پیگیری تهیه‌کننده (SCR-STAFF-712) و
  // صندوق تأیید استاد (SCR-STAFF-714).
  'med-rx-monitor': <RxMonitorPage />,
  'med-rx-only-visit': <RxOnlyVisitPage />,
  'med-nursing-cartable': <NursingCartablePage />,
  'med-approvals-mine': <MyApprovalsPage />,
  'med-approvals-inbox': <ApprovalInboxPage />,
  'med-insurer-admissions': <InsurerAdmissionOrdersPage />,
  'sch-referral-capacity': <ReferralCapacityPage />,
  'int-national-referral': <NationalReferralConnectionPage />,
};

/** نشانی‌های متن Prompt (`/staff`، `/staff/:id`) ← مسیر IA (`/settings/staff`) — C-AGENT-222. */
function StaffRedirect() {
  const { membershipId } = useParams();
  return <Navigate to={membershipId ? `${STAFF_PATH}/${membershipId}` : STAFF_PATH} replace />;
}

export interface StaffRouteOptions {
  apiBaseUrl: string;
  fetch?: typeof globalThis.fetch;
  /** مقدار ثابت قفل صفحه (تست). */
  lockMinutes?: number;
  /** اتصال زنده نوبت‌ها (پیش‌فرض SignalR؛ تست: اتصال ساختگی یا false). */
  schedulingRealtime?: SchedulingRealtimeFactory | false;
  /** اتصال زنده پیشرفت ورود داده (پیش‌فرض SignalR؛ تست: اتصال ساختگی یا false). */
  importRealtime?: ImportRealtimeFactory | false;
  /** اتصال زنده مرکز اعلان (پیش‌فرض SignalR؛ تست: اتصال ساختگی یا false). */
  notificationRealtime?: NotificationRealtimeFactory | false;
}

export function createStaffRoutes(options: StaffRouteOptions): RouteObject[] {
  const moduleRoutes: RouteObject[] = leafItems()
    .filter((item) => item.path !== '/')
    .map((item) => ({
      path: item.path.slice(1),
      handle: { crumbs: [{ labelKey: 'shell:menu.home', path: '/' }, { labelKey: item.labelKey }] },
      element: (
        <RequireAccess
          permission={item.permission}
          anyPermission={item.anyPermission}
          feature={item.feature}
          upgradePath={SUBSCRIPTION_PATH}
        >
          {PAGES[item.id] ?? <ComingSoonPage screenId={item.screenId ?? item.id} titleKey={item.labelKey} />}
        </RequireAccess>
      ),
    }));

  const tenantRoutes: RouteObject = {
    element: (
      <RequireAuthenticated>
        <TenantSessionProvider baseUrl={options.apiBaseUrl} fetch={options.fetch}>
          <AccessProvider>
            <Outlet />
          </AccessProvider>
        </TenantSessionProvider>
      </RequireAuthenticated>
    ),
    children: [
      { path: 'select-tenant', element: <TenantSelectPage /> },
      {
        // SCR-TABLET-001 (AP-DOC-01): حالت تحویل تبلت تمام‌صفحه بیرون از پوسته و منو؛ خروج فقط با PIN.
        path: 'tablet/:id',
        element: (
          <RequireTenantSession>
            <RequireAccess
              anyPermission={['doc.form.fill', 'doc.consent.collect']}
              upgradePath={SUBSCRIPTION_PATH}
            >
              <TabletModePage />
            </RequireAccess>
          </RequireTenantSession>
        ),
      },
      {
        // SCR-STAFF-617 (US-DOC-154): بارگذاری موبایل با توکن QR — بدون داده پرونده.
        path: 'm/upload/:token',
        element: (
          <RequireTenantSession>
            <MobileUploadPage />
          </RequireTenantSession>
        ),
      },
      {
        element: (
          <RequireTenantSession>
            <ScreenLock minutes={options.lockMinutes}>
              <SchedulingRealtimeProvider
                apiBaseUrl={options.apiBaseUrl}
                factory={options.schedulingRealtime}
              >
                <SchedulingActionsProvider>
                  <PatientActionsProvider>
                    <ImportRealtimeContext.Provider
                      value={{
                        apiBaseUrl: options.apiBaseUrl,
                        factory: options.importRealtime ?? signalRImportFactory,
                      }}
                    >
                      <NotificationRealtimeProvider
                        apiBaseUrl={options.apiBaseUrl}
                        factory={options.notificationRealtime ?? signalRNotificationFactory}
                      >
                        <StaffLayout />
                      </NotificationRealtimeProvider>
                    </ImportRealtimeContext.Provider>
                  </PatientActionsProvider>
                </SchedulingActionsProvider>
              </SchedulingRealtimeProvider>
            </ScreenLock>
          </RequireTenantSession>
        ),
        children: [
          { index: true, element: <HomePage /> },
          ...moduleRoutes,
          {
            path: `${STAFF_PATH.slice(1)}/:membershipId`,
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'iam:menu.staff', path: STAFF_PATH },
                { labelKey: 'iam:detail.crumb' },
              ],
            },
            element: (
              <RequireAccess permission="iam.member.view" upgradePath={SUBSCRIPTION_PATH}>
                <StaffDetailPage />
              </RequireAccess>
            ),
          },
          {
            path: 'account',
            handle: {
              crumbs: [{ labelKey: 'shell:menu.home', path: '/' }, { labelKey: 'iam:account.title' }],
            },
            element: <AccountPage />,
          },
          // SCR-STAFF-994 (AP-CHN-02): جزئیات تیکت بیرون از منو (پیوند صف، اعلان، خط زمانی و تماس).
          {
            path: 'tickets/:id',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'chn:menu.tickets', path: '/tickets' },
                { labelKey: 'chn:tickets.detail.crumb' },
              ],
            },
            element: (
              <RequireAccess permission="chn.ticket.create" feature="feature.chn.ticketing" upgradePath={SUBSCRIPTION_PATH}>
                <TicketDetailPage />
              </RequireAccess>
            ),
          },
          // SCR-STAFF-936 (AP-ORG-02): شناسنامه دستگاه بیرون از منو (پیوند فهرست دستگاه‌ها و اعلان مغایرت).
          {
            path: 'settings/devices/:id',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'org:menu.devices', path: '/settings/devices' },
                { labelKey: 'org:device_detail.crumb' },
              ],
            },
            element: (
              <RequireAccess
                anyPermission={['org.device.manage', 'org.device.view']}
                upgradePath={SUBSCRIPTION_PATH}
              >
                <DeviceDetailPage />
              </RequireAccess>
            ),
          },
          // SCR-STAFF-502 (AP-CLN-01): مواجهه بیرون از منو (پیوند خط زمانی و کارهای معوق).
          {
            path: 'desk/encounters/:id',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'cln:menu.desk', path: '/desk' },
                { labelKey: 'cln:encounter.title' },
              ],
            },
            element: (
              <RequireAccess permission="cln.record.view" upgradePath={SUBSCRIPTION_PATH}>
                <EncounterPage />
              </RequireAccess>
            ),
          },
          // SCR-STAFF-1007 (AP-INT-09): تمدید اعتبارنامه بیمه‌گر، بیرون از منو (مقصد لینک پیامکی — US-INT-107).
          {
            path: 'settings/integrations/insurer-credentials/renew',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'int:menu.insurer_credentials', path: '/settings/integrations/insurer-credentials' },
                { labelKey: 'int:credential_renewal.title' },
              ],
            },
            element: (
              <RequireAccess permission="int.credential.manage_own" upgradePath={SUBSCRIPTION_PATH}>
                <CredentialRenewalPage />
              </RequireAccess>
            ),
          },
          // SCR-STAFF-1008 (AP-INT-09): لاگ فراخوان یک موجودیت، بیرون از منو (از پایش نسخه‌ها باز می‌شود — US-INT-110).
          {
            path: 'integrations/entity-calls',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'int:entity_calls.title' },
              ],
            },
            element: (
              <RequireAccess anyPermission={['int.log.view_own', 'int.log.view']} upgradePath={SUBSCRIPTION_PATH}>
                <EntityCallLogPage />
              </RequireAccess>
            ),
          },
          // SCR-STAFF-704 (AP-INT-05): نمایشگر DICOM یک نتیجه تصویربرداری، بیرون از منو (از نتیجه و پرونده باز می‌شود).
          {
            path: 'desk/results/:resultId/imaging',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'med:menu.orders', path: '/desk/orders' },
                { labelKey: 'med:imaging.crumb' },
              ],
            },
            element: (
              <RequireAccess permission="med.order.view" upgradePath={SUBSCRIPTION_PATH}>
                <ImagingViewerPage />
              </RequireAccess>
            ),
          },
          // SCR-STAFF-503 (AP-CLN-02): طرح درمان و پیشرفت بیرون از منو (پرونده، مواجهه و کارهای معوق).
          {
            path: 'desk/plans/:id',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'cln:menu.desk', path: '/desk' },
                { labelKey: 'cln:plan.crumb' },
              ],
            },
            element: (
              <RequireAccess permission="cln.record.view" upgradePath={SUBSCRIPTION_PATH}>
                <TreatmentPlanPage />
              </RequireAccess>
            ),
          },
          // SCR-STAFF-201 (AP-CAT-01): جزئیات خدمت بیرون از منو.
          {
            path: 'settings/services/:id',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'cat:menu.services', path: '/settings/services' },
                { labelKey: 'cat:detail.crumb' },
              ],
            },
            element: (
              <RequireAccess permission="cat.service.view" upgradePath={SUBSCRIPTION_PATH}>
                <ServiceDetailPage />
              </RequireAccess>
            ),
          },
          // AP-PAT-02: صف تکراری‌ها، ویزارد ادغام و پرونده‌های ناقص (مسیر ثابت پیش از /patients/:id انتخاب می‌شود).
          ...(
            [
              [
                'patients/duplicates',
                'pat:duplicates_queue.title',
                'pat.duplicates.review',
                <DuplicatesPage key="104" />,
              ],
              ['patients/merge', 'pat:merge.title', 'pat.patient.merge', <MergeWizardPage key="105" />],
              [
                'patients/incomplete',
                'pat:temporary.queue',
                'pat.patient.update',
                <IncompletePatientsPage key="107" />,
              ],
            ] as const
          ).map(([path, labelKey, permission, page]) => ({
            path,
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'pat:menu.patients', path: '/patients' },
                { labelKey },
              ],
            },
            element: (
              <RequireAccess permission={permission} upgradePath={SUBSCRIPTION_PATH}>
                {page}
              </RequireAccess>
            ),
          })),
          // AP-PAT-03: صف درخواست‌های مراجعه‌کنندگان (SCR-STAFF-106) با هر یک از مجوزهای تصمیم.
          {
            path: 'patients/requests',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'pat:menu.patients', path: '/patients' },
                { labelKey: 'pat:requests.title' },
              ],
            },
            element: (
              <RequireAccess
                anyPermission={['pat.patient.update', 'pat.patient.export', 'pat.patient.update_identity']}
                upgradePath={SUBSCRIPTION_PATH}
              >
                <DataRequestsPage />
              </RequireAccess>
            ),
          },
          {
            // SCR-STAFF-960 (AP-ENG-01): جزئیات سرنخ.
            path: 'crm/leads/:id',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'eng:menu.leads', path: '/crm/leads' },
                { labelKey: 'eng:lead_detail.crumb' },
              ],
            },
            element: (
              <RequireAccess
                permission="eng.lead.view"
                feature="feature.eng.crm"
                upgradePath={SUBSCRIPTION_PATH}
              >
                <LeadDetailPage />
              </RequireAccess>
            ),
          },
          {
            // SCR-STAFF-961 (AP-ENG-01): مشاوره تازه (از سرنخ یا مراجعه‌کننده) و جزئیات مشاوره.
            path: 'crm/consultations/new',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'eng:menu.queue', path: '/crm/queue' },
                { labelKey: 'eng:consultation.crumb' },
              ],
            },
            element: (
              <RequireAccess
                permission="eng.consultation.manage"
                feature="feature.eng.crm"
                upgradePath={SUBSCRIPTION_PATH}
              >
                <ConsultationPage />
              </RequireAccess>
            ),
          },
          {
            path: 'crm/consultations/:id',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'eng:menu.queue', path: '/crm/queue' },
                { labelKey: 'eng:consultation.crumb' },
              ],
            },
            element: (
              <RequireAccess
                permission="eng.consultation.manage"
                feature="feature.eng.crm"
                upgradePath={SUBSCRIPTION_PATH}
              >
                <ConsultationPage />
              </RequireAccess>
            ),
          },
          {
            // SCR-STAFF-102 (AP-PAT-01): پرونده مراجعه‌کننده؛ شناسه UUID در URL (هرگز شماره پرونده یا کد ملی — IA §2.3).
            path: 'patients/:id',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'pat:menu.patients', path: '/patients' },
                { labelKey: 'pat:profile.title' },
              ],
            },
            element: (
              <RequireAccess permission="pat.patient.view" upgradePath={SUBSCRIPTION_PATH}>
                <PatientProfilePage />
              </RequireAccess>
            ),
          },
          {
            // SCR-STAFF-926 (AP-IAM-04): گزارش دسترسی به پرونده از صفحه مراجعه‌کننده (C-AGENT-404).
            path: 'patients/:patientId/access-report',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'pat:menu.patients', path: '/patients' },
                { labelKey: 'iam:access_report.title' },
              ],
            },
            element: (
              <RequireAccess permission="iam.audit.view_patient_access" upgradePath={SUBSCRIPTION_PATH}>
                <PatientAccessReportPage />
              </RequireAccess>
            ),
          },
          {
            // SCR-STAFF-602 (AP-DOC-01): تکمیل و بازبینی فرم (مقصد خط زمانی پرونده).
            path: 'documents/forms/:id',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'pat:menu.patients', path: '/patients' },
                { labelKey: 'doc:forms.crumb' },
              ],
            },
            element: (
              <RequireAccess permission="doc.document.view" upgradePath={SUBSCRIPTION_PATH}>
                <FormSubmissionPage />
              </RequireAccess>
            ),
          },
          // AP-BIL-01: مراجعه (SCR-STAFF-400، ورود از نوبت با ردیف برگه روز) و جزئیات صورتحساب (SCR-STAFF-403)؛ شناسه UUID در URL.
          ...(
            [
              [
                'visits/by-appointment/:appointmentId',
                'bil:menu.visits',
                '/visits',
                'bil:visit.title',
                <VisitByAppointmentPage key="400a" />,
              ],
              ['visits/:id', 'bil:menu.visits', '/visits', 'bil:visit.title', <VisitPage key="400" />],
              [
                'invoices/:id',
                'bil:menu.invoices',
                '/invoices',
                'bil:invoice.crumb',
                <InvoicePage key="403" />,
              ],
            ] as const
          ).map(([path, parentKey, parentPath, labelKey, page]) => ({
            path,
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: parentKey, path: parentPath },
                { labelKey },
              ],
            },
            element: (
              <RequireAccess permission="bil.visit.view" upgradePath={SUBSCRIPTION_PATH}>
                {page}
              </RequireAccess>
            ),
          })),
          {
            // SCR-STAFF-870 (AP-INV-01): جزئیات کالا، واحدها و بچ‌ها.
            path: 'inventory/items/:id',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'inv:menu.items', path: '/inventory/items' },
                { labelKey: 'inv:item.crumb' },
              ],
            },
            element: (
              <RequireAccess permission="inv.item.view" upgradePath={SUBSCRIPTION_PATH}>
                <ItemDetailPage />
              </RequireAccess>
            ),
          },
          {
            // SCR-STAFF-875 (AP-INV-02): برگه شمارش انبارگردانی.
            path: 'inventory/stocktakes/:id',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'inv:menu.stocktakes', path: '/inventory/stocktakes' },
                { labelKey: 'inv:stocktakes.crumb' },
              ],
            },
            element: (
              <RequireAccess
                permission="inv.stocktake.manage"
                feature="feature.inv.inventory"
                upgradePath={SUBSCRIPTION_PATH}
              >
                <StocktakePage />
              </RequireAccess>
            ),
          },
          {
            // SCR-STAFF-882 (AP-ACC-01): جزئیات سند و Drill-down.
            path: 'accounting/journals/:id',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'acc:menu.journals', path: '/accounting/journals' },
                { labelKey: 'acc:journal.crumb' },
              ],
            },
            element: (
              <RequireAccess permission="acc.journal.view" upgradePath={SUBSCRIPTION_PATH}>
                <JournalDetailPage />
              </RequireAccess>
            ),
          },
          {
            // SCR-STAFF-401 (AP-BIL-02): تسویه مراجعه از Alt+P صفحه مراجعه (IA §2.3: /cashier/checkout/:visitId).
            path: 'cashier/checkout/:visitId',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'bil:menu.cashier', path: '/cashier' },
                { labelKey: 'bil:cashier.checkout' },
              ],
            },
            element: (
              <RequireAccess permission="bil.payment.receive" upgradePath={SUBSCRIPTION_PATH}>
                <CashierPage />
              </RequireAccess>
            ),
          },
          {
            // SCR-STAFF-407 (AP-BIL-03): ایجاد برآورد (FLOW-12؛ از پرونده، فهرست یا شبیه‌ساز CAT).
            path: 'quotes/new',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'bil:menu.quotes', path: '/quotes' },
                { labelKey: 'bil:quotes.new' },
              ],
            },
            element: (
              <RequireAccess permission="bil.quote.create" upgradePath={SUBSCRIPTION_PATH}>
                <QuoteEditorPage />
              </RequireAccess>
            ),
          },
          {
            // SCR-STAFF-407 (AP-BIL-03): جزئیات و چرخه برآورد.
            path: 'quotes/:id',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'bil:menu.quotes', path: '/quotes' },
                { labelKey: 'bil:quotes.detail' },
              ],
            },
            element: (
              <RequireAccess permission="bil.visit.view" upgradePath={SUBSCRIPTION_PATH}>
                <QuoteDetailPage />
              </RequireAccess>
            ),
          },
          {
            // SCR-STAFF-408 (AP-BIL-02): پروفایل مالی مراجعه‌کننده (IA §2.3: /patients/:id/billing).
            path: 'patients/:id/billing',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'pat:menu.patients', path: '/patients' },
                { labelKey: 'bil:finance.title' },
              ],
            },
            element: (
              <RequireAccess permission="bil.visit.view" upgradePath={SUBSCRIPTION_PATH}>
                <PatientFinancePage />
              </RequireAccess>
            ),
          },
          {
            // SCR-STAFF-303 (AP-SCH-01): جزئیات نوبت؛ شناسه UUID در URL.
            path: 'appointments/:id',
            handle: {
              crumbs: [
                { labelKey: 'shell:menu.home', path: '/' },
                { labelKey: 'sch:menu.today', path: '/today' },
                { labelKey: 'sch:detail.crumb' },
              ],
            },
            element: (
              <RequireAccess permission="sch.appointment.view" upgradePath={SUBSCRIPTION_PATH}>
                <AppointmentDetailPage />
              </RequireAccess>
            ),
          },
          {
            // SCR-STAFF-900 (AP-PLT-04): Setup Wizard؛ ورود از کارت خانه (بیرون از منو — C-AGENT-372).
            path: SETUP_PATH.slice(1),
            handle: {
              crumbs: [{ labelKey: 'shell:menu.home', path: '/' }, { labelKey: 'plt:setup.title' }],
            },
            element: (
              <RequireAccess permission={ONBOARDING_PERMISSION} upgradePath={SUBSCRIPTION_PATH}>
                <SetupWizardPage />
              </RequireAccess>
            ),
          },
          { path: 'staff', element: <StaffRedirect /> },
          { path: 'staff/:membershipId', element: <StaffRedirect /> },
          { path: 'forbidden', element: <ForbiddenState /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  };

  return [
    {
      // صفحات عمومی (بدون ورود و بدون نشست مستأجر — AP-IAM-02) و بقیه با ورود و نشست مستأجر.
      element: (
        <PublicApiProvider baseUrl={options.apiBaseUrl} fetch={options.fetch}>
          <Outlet />
        </PublicApiProvider>
      ),
      errorElement: <RouteErrorPage />,
      children: [
        { path: 'invitations/:token', element: <InvitationPage /> },
        { path: 'invite/:token', element: <InvitationPage /> },
        { path: 'recover', element: <RecoverPage /> },
        // AP-IAM-05: تبلت جفت‌شده با حساب دستگاه (بدون ورود کاربر؛ فقط جلسه تحویل سپرده‌شده).
        { path: 'device', element: <DeviceModePage apiBaseUrl={options.apiBaseUrl} fetch={options.fetch} /> },
        // AP-DOC-01: صفحات عمومی لینک امن (SCR-PUBLIC-010/011/012).
        { path: 'f/:token', element: <PublicFormPage /> },
        { path: 'forms/:token', element: <PublicStudioFormPage /> },
        { path: 's/:token', element: <PublicSignaturePage /> },
        { path: 'd/:token', element: <PublicSharePage /> },
        // AP-BIL-02: صفحه عمومی پرداخت لینک (SCR-PAY-001).
        { path: 'pay/:token', element: <PublicPayPage /> },
        // AP-ENG-02: صفحه عمومی نظرسنجی پس از ویزیت (SCR-PUBLIC-030).
        { path: 'survey/:token', element: <PublicSurveyPage /> },
        // AP-MED-01: تأیید اصالت QR نسخه و برگه درخواست (SCR-PUBLIC-014).
        { path: 'v/:code', element: <PublicVerifyPage /> },
        // AP-CLN-04: تأیید اصالت QR گواهی (SCR-PUBLIC-013؛ US-DOC-052).
        { path: 'cv/:code', element: <PublicCertificateVerifyPage /> },
        // AP-PLT-07: ثبت‌نام (SCR-PUBLIC-001)، بازگشت درگاه اشتراک، پذیرش مالکیت و دانلود خروجی (C-AGENT-1171).
        { path: 'signup', element: <SignupPage /> },
        { path: 'subscription-payment/:token', element: <SubscriptionPaymentReturnPage /> },
        { path: 'ownership/:token', element: <OwnershipAcceptPage /> },
        { path: 'exports/:token', element: <ExportDownloadPage /> },
        // AP-PAT-03: دریافت نسخه داده مراجعه‌کننده با لینک پیامکی و OTP (SCR-PUBLIC-015).
        { path: 'data-exports/:token', element: <DataExportPublicPage /> },
        tenantRoutes,
      ],
    },
  ];
}
